import { type CompiledRule, type Disposition, loadBaselineRules } from './rules.ts';

/**
 * A single detection within one decoded string. Carries the location
 * (offsets) and classification — never the matched secret bytes. The
 * redactor (TAN-4341) re-reads the bytes at [start, end) only when needed.
 */
export type RawMatch = {
  ruleId: string;
  disposition: Disposition;
  start: number; // char offset into the scanned string, inclusive
  end: number; // exclusive; always > start
};

/** Shannon entropy in bits per character (base-2). */
export function shannonEntropy(s: string): number {
  if (s.length === 0) return 0;
  const freq = new Map<string, number>();
  for (const ch of s) freq.set(ch, (freq.get(ch) ?? 0) + 1);
  let h = 0;
  for (const count of freq.values()) {
    const p = count / s.length;
    h -= p * Math.log2(p);
  }
  return h;
}

// Resolve the [start, end) span of a match's secret group, falling back to the
// whole match when the group is absent. Relies on the `d` (hasIndices) flag.
function spanForMatch(match: RegExpMatchArray, secretGroup: number): [number, number] | null {
  const indices = match.indices;
  if (secretGroup > 0 && indices?.[secretGroup]) {
    return indices[secretGroup] as [number, number];
  }
  if (indices?.[0]) return indices[0] as [number, number];
  const start = match.index ?? 0;
  return [start, start + match[0].length];
}

/**
 * Run the detector rules against a single decoded string. Keyword-prefilters
 * each rule (skip unless a keyword is present, case-insensitive), then reports
 * every match's secret-group span. Entropy-gated rules drop matches whose
 * secret falls below their threshold. Results are sorted by (start, ruleId).
 */
export type DetectOptions = {
  /**
   * Skip the per-value keyword prefilter. The caller has already gated these
   * rules by keyword presence at a coarser scope (e.g. the whole transcript),
   * which is required for correctness: a secret can appear on a JSON leaf that
   * lacks the rule's keyword even though the keyword is present elsewhere.
   */
  skipKeywordPrefilter?: boolean;
};

export function detectSecrets(
  value: string,
  rules: CompiledRule[] = loadBaselineRules(),
  options: DetectOptions = {},
): RawMatch[] {
  const lowered = value.toLowerCase();
  const out: RawMatch[] = [];

  for (const rule of rules) {
    if (
      !options.skipKeywordPrefilter &&
      rule.keywords.length > 0 &&
      !rule.keywords.some((kw) => lowered.includes(kw))
    ) {
      continue;
    }
    // matchAll clones the regexp internally, so the shared matcher's lastIndex
    // does not leak across calls.
    for (const match of value.matchAll(rule.matcher)) {
      const span = spanForMatch(match, rule.secretGroup);
      if (!span) continue;
      const [start, end] = span;
      if (end <= start) continue;
      if (rule.entropy != null && shannonEntropy(value.slice(start, end)) < rule.entropy) {
        continue;
      }
      out.push({ ruleId: rule.id, disposition: rule.disposition, start, end });
    }
  }

  out.sort((a, b) => a.start - b.start || (a.ruleId < b.ruleId ? -1 : a.ruleId > b.ruleId ? 1 : 0));
  return out;
}
